import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { HpIcon } from './icons.js';

export interface HpMultiselectItem {
  value: string;
  label: string;
  /** Small secondary line under the label (e.g. the platform of a campaign). */
  description?: string;
  /** Whether the item is "active now"; drives the optional active-only toggle (`activeOnlyLabel`). */
  active?: boolean;
}

export interface HpMultiselectProps {
  label: ReactNode;
  /** Shown when nothing is selected, and as the "clear" button. */
  allLabel: string;
  /** Heading of the popover, e.g. "Pick any combination". */
  heading: string;
  /** Footnote of the popover, e.g. "Nothing selected means everything". */
  hint: string;
  items: readonly HpMultiselectItem[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  className?: string;
  /** Placeholder of the search field; the field appears only when set. Matching ignores case and diacritics. */
  searchPlaceholder?: string;
  /** Label of the "only active" toggle; the toggle appears only when set and some item carries `active`. */
  activeOnlyLabel?: string;
  /** Tooltip of the toggle explaining what "active" means. */
  activeOnlyTitle?: string;
  /** Label of the "select all shown" action, offered when the list is narrowed by search or the toggle. */
  selectShownLabel?: string;
  /** Shown instead of the options when nothing matches. */
  emptyLabel?: string;
}

/** Case- and diacritics-insensitive form of a label for matching. */
export function foldForSearch(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/**
 * The design system's checkbox multi-selection (`hp-multiselect`): a `details` field whose summary
 * shows the chosen labels (or "all") and a popover of checkboxes. Closes on outside click and Escape.
 * Optionally narrows the list with a search field and an "only active" toggle (HPD-27) and lets the
 * reader select everything the narrowed list shows; selections hidden by the narrowing stay selected.
 */
export function HpMultiselect({
  label,
  allLabel,
  heading,
  hint,
  items,
  selected,
  onChange,
  className,
  searchPlaceholder,
  activeOnlyLabel,
  activeOnlyTitle,
  selectShownLabel,
  emptyLabel,
}: HpMultiselectProps) {
  const id = useId();
  const ref = useRef<HTMLDetailsElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onDocClick = (e: MouseEvent) => {
      if (el.open && e.target instanceof Node && !el.contains(e.target)) el.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && el.open) {
        el.open = false;
        el.querySelector('summary')?.focus({ preventScroll: true });
      }
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  const hasActiveFlags = items.some((i) => i.active !== undefined);
  const showActiveToggle = Boolean(activeOnlyLabel) && hasActiveFlags;
  const folded = foldForSearch(query);
  const shown = useMemo(
    () =>
      items.filter(
        (i) =>
          (!activeOnly || i.active === true) &&
          (folded === '' || foldForSearch(`${i.label} ${i.description ?? ''}`).includes(folded)),
      ),
    [items, activeOnly, folded],
  );
  const narrowed = folded !== '' || (showActiveToggle && activeOnly);
  const chosen = items.filter((i) => selected.includes(i.value)).map((i) => i.label);
  const summary = chosen.length ? chosen.join(', ') : allLabel;
  const onToggle = () => {
    if (ref.current?.open) searchRef.current?.focus({ preventScroll: true });
    else setQuery('');
  };
  return (
    <div className={['pa-field', 'hp-field', 'hp-multiselect-field', className].filter(Boolean).join(' ')}>
      <span id={`${id}-label`}>{label}</span>
      <details className="hp-multiselect" ref={ref} onToggle={onToggle}>
        <summary id={id} aria-labelledby={`${id}-label ${id}-value`} title={summary}>
          <span id={`${id}-value`} className="hp-multiselect__value">
            {summary}
          </span>
          {chosen.length > 1 && (
            <span className="hp-multiselect__count" aria-hidden="true">
              {chosen.length}
            </span>
          )}
          <HpIcon name="chevronDown" />
        </summary>
        <div className="hp-multiselect__popover">
          <div className="hp-multiselect__heading">
            <span>{heading}</span>
            <button type="button" onClick={() => onChange([])}>
              {allLabel}
            </button>
          </div>
          {(searchPlaceholder || showActiveToggle) && (
            <div className="hp-multiselect__tools">
              {searchPlaceholder && (
                <div className="hp-multiselect__search">
                  <HpIcon name="search" />
                  <input
                    ref={searchRef}
                    type="search"
                    value={query}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    autoComplete="off"
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
              )}
              {showActiveToggle && (
                <label className="hp-multiselect__toggle" title={activeOnlyTitle}>
                  <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                  <span>{activeOnlyLabel}</span>
                </label>
              )}
            </div>
          )}
          <div className="hp-multiselect__options" role="group" aria-labelledby={`${id}-label`}>
            {shown.length === 0 && emptyLabel && <p className="hp-multiselect__empty">{emptyLabel}</p>}
            {shown.map((item) => (
              <label key={item.value} className="hp-multiselect__option">
                <input
                  type="checkbox"
                  id={`${id}-option-${item.value}`}
                  value={item.value}
                  checked={selected.includes(item.value)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...new Set([...selected, item.value])]
                        : selected.filter((v) => v !== item.value),
                    )
                  }
                />
                <span>
                  {item.label}
                  {item.description && <small>{item.description}</small>}
                </span>
              </label>
            ))}
          </div>
          {narrowed && selectShownLabel && shown.length > 0 && (
            <div className="hp-multiselect__actions">
              <button type="button" onClick={() => onChange([...new Set([...selected, ...shown.map((i) => i.value)])])}>
                {selectShownLabel} ({shown.length})
              </button>
            </div>
          )}
          <p className="hp-multiselect__hint">{hint}</p>
        </div>
      </details>
    </div>
  );
}
